import { imageUrlZodType } from "@@/shared/images/image-url";
import { paddingZodSchema } from "@@/shared/stylable/padding";
import { setWithinRange } from "@@/utilities/set-within-range";
import { z } from "zod";
import { type ImageRef, imageRefZodSchema } from "../images/image-ref";
import { colorAsString } from "./color-theme/color-as-string";
import { type ColorItem, colorItemZodSchema } from "./color-theme/color-item";
import { type FitType, fitTypes } from "./fit";
import { type RepeatType, repeatTypes } from "./repeat-type";

type LinearGradientSimple = {
    _type: "LINEAR_GRADIENT_SIMPLE";
    from: ColorItem;
    to: ColorItem;
    degrees?: number;
};

const linearGradientSimpleZodSchema: z.ZodSchema<LinearGradientSimple> =
    z.object({
        _type: z.literal("LINEAR_GRADIENT_SIMPLE"),
        from: colorItemZodSchema,
        to: colorItemZodSchema,
        degrees: z.number(),
    });

type LinearGradient = {
    _type: "LINEAR_GRADIENT";
    steps: {
        color: ColorItem;
        percentage: number;
    }[];
    degrees?: number;
};

const gradientToCssValue = (
    gradient?: LinearGradient | LinearGradientSimple,
): string | undefined => {
    if (!gradient) return undefined;
    switch (gradient._type) {
        case "LINEAR_GRADIENT":
            return `linear-gradient(${gradient.degrees || 0}deg,${gradient.steps
                .map(step => {
                    return `${colorAsString(step.color)} ${setWithinRange(
                        Math.round(step.percentage * 100),
                        {
                            min: 0,
                            max: 100,
                        },
                    )}%`;
                })
                .join(",")})`;
        case "LINEAR_GRADIENT_SIMPLE":
        default:
            return `linear-gradient(${
                gradient.degrees || 0
            }deg, ${colorAsString(gradient.from)}, ${colorAsString(
                gradient.to,
            )})`;
    }
};

const linearGradientZodSchema: z.ZodSchema<LinearGradient> = z.object({
    _type: z.literal("LINEAR_GRADIENT"),
    steps: z.array(
        z.object({
            color: colorItemZodSchema,
            percentage: z.number().min(0).max(1),
        }),
    ),
    degrees: z.number(),
});

type BackgroundStyle = {
    color?: ColorItem;
    imageReference?: ImageRef;
    imageUrl?: string;
    imageSize?: { imageWidth?: number; imageHeight?: number };
    fit?: FitType;
    repeat?: RepeatType;
    linearGradient?: LinearGradient | LinearGradientSimple;
    borderRadius?: number;
};

const backgroundStyleZodSchema: z.ZodSchema<BackgroundStyle> = z.object({
    color: colorItemZodSchema,
    imageReference: imageRefZodSchema,
    imageUrl: imageUrlZodType,
    imageSize: z
        .object({
            imageWidth: z.number().optional(),
            imageHeight: z.number().optional(),
        })
        .optional(),
    fit: z.enum(fitTypes).optional(),
    repeat: z.enum(repeatTypes).optional(),
    linearGradient: z.union([
        linearGradientZodSchema,
        linearGradientSimpleZodSchema,
    ]),
    borderRadius: z.number().optional(),
    padding: paddingZodSchema.optional(),
});

export {
    backgroundStyleZodSchema,
    gradientToCssValue,
    linearGradientSimpleZodSchema,
    linearGradientZodSchema,
};
export type { BackgroundStyle, LinearGradient, LinearGradientSimple };
